import React, { useContext, useEffect, useState } from 'react'
import Select from 'components/select'
import Button from 'components/button'
import { Pencil } from 'components/icons'
import api from 'helpers/api'
import { ITableViewColumn } from 'types'
import { buildSlicerRuntimeFilters } from 'components/spreadsheet/helpers/functions'
import { useSlicers } from 'providers/slicers'
import { EditorModeContext } from '../EditorModeContext'

export interface SlicerProps {
  blockProps: {
    tableId: string
    viewId: string
    columnId: string
    entityKey?: string
    openSlicerDialog?: (anchor: HTMLElement, entityKey: string) => void
  }
}

const Slicer: React.FC<SlicerProps> = ({ blockProps }) => {
  const { selections, registerSlicer, setSlicer } = useSlicers()
  const { editorFocused, readOnly } = useContext(EditorModeContext)
  const [sourceColumns, setSourceColumns] = useState<ITableViewColumn[]>([])
  const [columnName, setColumnName] = useState<string>('')
  const [options, setOptions] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const { viewId, columnId } = blockProps

  useEffect(() => {
    registerSlicer(columnId)
  }, [columnId, registerSlicer])

  useEffect(() => {
    let cancelled = false
    api
      .getTableView(viewId)
      .then((response) => {
        if (cancelled) return
        const cols = response.data.columns ?? []
        setSourceColumns(cols)
        setColumnName(cols.find((c) => c.publicId === columnId)?.name ?? '')
      })
      .catch(() => !cancelled && setSourceColumns([]))
    return () => {
      cancelled = true
    }
  }, [viewId, columnId])

  const otherSelections: Record<string, string[]> = {}
  for (const [key, values] of Object.entries(selections)) {
    if (key !== columnId) otherSelections[key] = values
  }
  const cascadeFilters = buildSlicerRuntimeFilters(sourceColumns, otherSelections)
  const cascadeFiltersKey = JSON.stringify(cascadeFilters)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .getTableViewDistinct(viewId, columnId, cascadeFilters)
      .then((response) => {
        if (cancelled) return
        setOptions((response.data ?? []).filter((v): v is string => typeof v === 'string' && v.length > 0))
      })
      .catch(() => !cancelled && setOptions([]))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [viewId, columnId, cascadeFiltersKey])

  const selected = selections[columnId] ?? []
  const selectOptions = options.map((value) => ({ value, label: value }))

  const handleChange = (next: string[]) => setSlicer(columnId, next)

  const showEdit = blockProps.openSlicerDialog && editorFocused && !readOnly && blockProps.entityKey

  return (
    // Keys pressed inside the slicer (its Edit Slicer button, its value list) stay with it, or the document editor
    // would act on the insert its cursor is on (see useEmbedControls). Escape still reaches the document, where the
    // value list's menu listens for it; the editor does nothing with it.
    <div
      className="w-full mb-[5px]"
      data-testid="embedded-slicer"
      data-slicer-column-id={columnId}
      onKeyDown={readOnly ? undefined : (event) => event.key !== 'Escape' && event.stopPropagation()}
    >
      {showEdit && (
        <Button
          type="button"
          className="flex mb-[5px] z-[9999] pointer-events-auto hover:border-divider-strong hover:shadow-[0_2px_6px_rgba(0,0,0,0.18)] focus:border-divider-strong focus:shadow-[0_2px_6px_rgba(0,0,0,0.18)]"
          title="Edit slicer"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => blockProps.openSlicerDialog!(e.currentTarget, blockProps.entityKey!)}
        >
          <Pencil className="mr-[5px] w-3.5 h-3.5" />
          Edit Slicer
        </Button>
      )}
      {/* The selected-insert outline goes round this row */}
      <div className="flex items-center gap-2.5" data-embed-view="">
        {columnName && <span className="text-muted italic min-w-[80px]">{columnName}</span>}
        <div className="flex-1">
          <Select
            multiselect
            options={selectOptions}
            optionsSelected={selected}
            setOptionsSelected={handleChange}
            onOptionClick={(option) =>
              handleChange(selected.includes(option) ? selected.filter((v) => v !== option) : [...selected, option])
            }
            loading={loading}
            placeholder={loading ? 'Loading…' : 'All values'}
          />
        </div>
      </div>
    </div>
  )
}

export default Slicer
